import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import {
    useAdminAuth0BulkSyncCandidates,
    useAdminAuth0BulkSyncMutations,
} from '../../hooks/useApi';
import type {
    AdminAuth0BulkSyncCandidate,
    AdminAuth0BulkSyncEvent,
    AdminAuth0BulkSyncJobSnapshot,
} from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

type FailureRow = {
    companyId?: number;
    userId?: number;
    email?: string;
    error: string;
};

type RunProgress = {
    totalCompanies: number;
    completedCompanies: number;
    failedCompanies: number;
    totalUsers: number;
    syncedUsers: number;
    skippedUsers: number;
    failedUsers: number;
};

const CANDIDATE_PAGE_SIZE = 500;

export function AdminAuth0BulkSyncHub() {
    const [unsyncedOnly, setUnsyncedOnly] = useState(true);
    const [hideUnnamedCompanies, setHideUnnamedCompanies] = useState(false);
    const [candidateListCollapsed, setCandidateListCollapsed] = useState(false);
    const [candidatePage, setCandidatePage] = useState(1);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<number>>(new Set());
    const [job, setJob] = useState<AdminAuth0BulkSyncJobSnapshot | null>(null);
    const [events, setEvents] = useState<AdminAuth0BulkSyncEvent[]>([]);
    const [failures, setFailures] = useState<FailureRow[]>([]);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [isStartingStream, setIsStartingStream] = useState(false);
    const cleanupRef = useRef<(() => void) | null>(null);
    const candidatesQuery = useAdminAuth0BulkSyncCandidates(unsyncedOnly);
    const { startJobStream, stopJob } = useAdminAuth0BulkSyncMutations();
    const candidates = useMemo(
        () => candidatesQuery.data?.candidates ?? [],
        [candidatesQuery.data?.candidates],
    );
    const filteredCandidates = useMemo(
        () =>
            hideUnnamedCompanies
                ? candidates.filter((candidate) => !isUnnamedCompany(candidate))
                : candidates,
        [candidates, hideUnnamedCompanies],
    );
    const totalCandidatePages = Math.max(
        1,
        Math.ceil(filteredCandidates.length / CANDIDATE_PAGE_SIZE),
    );
    const safeCandidatePage = Math.min(candidatePage, totalCandidatePages);
    const pagedCandidates = filteredCandidates.slice(
        (safeCandidatePage - 1) * CANDIDATE_PAGE_SIZE,
        safeCandidatePage * CANDIDATE_PAGE_SIZE,
    );
    const isRunning = job?.status === 'running' || job?.status === 'stopping';
    const runProgress = useMemo(
        () => calculateRunProgress(events, job, selectedCompanyIds.size),
        [events, job, selectedCompanyIds.size],
    );
    const completedCompanies = runProgress.completedCompanies;
    const failedCompanies = runProgress.failedCompanies;
    const processedCompanies = completedCompanies + failedCompanies;
    const totalCompanies = runProgress.totalCompanies;
    const progressPercent =
        totalCompanies > 0 ? Math.round((processedCompanies / totalCompanies) * 100) : 0;

    useEffect(() => {
        return () => {
            cleanupRef.current?.();
        };
    }, []);

    useEffect(() => {
        setCandidatePage(1);
    }, [hideUnnamedCompanies, candidates]);

    const toggleCompany = (companyId: number) => {
        setSelectedCompanyIds((current) => {
            const next = new Set(current);
            if (next.has(companyId)) {
                next.delete(companyId);
            } else {
                next.add(companyId);
            }
            return next;
        });
    };

    const selectAll = () => {
        setSelectedCompanyIds(new Set(filteredCandidates.map((candidate) => candidate.companyId)));
    };

    const clearSelection = () => {
        setSelectedCompanyIds(new Set());
    };

    const handleEvent = (event: AdminAuth0BulkSyncEvent) => {
        setEvents((current) => [...current, event].slice(-200));
        if (event.type === 'job_start') {
            setJob({
                jobId: event.jobId,
                status: 'running',
                companyIds: Array.from(selectedCompanyIds),
                unsyncedOnly,
                totalCompanies:
                    typeof event.totalCompanies === 'number'
                        ? event.totalCompanies
                        : selectedCompanyIds.size,
                completedCompanies: 0,
                failedCompanies: 0,
                skippedCompanies: 0,
                totalUsers: 0,
                syncedUsers: 0,
                skippedUsers: 0,
                failedUsers: 0,
                companyResults: [],
                recentEvents: [],
                stopRequested: false,
                createdAt: event.timestamp ?? new Date().toISOString(),
                updatedAt: event.timestamp ?? new Date().toISOString(),
            });
        }
        if (event.type === 'user_failure') {
            setFailures((current) => [
                ...current,
                {
                    companyId: event.companyId,
                    userId: typeof event.userId === 'number' ? event.userId : undefined,
                    email: typeof event.email === 'string' ? event.email : undefined,
                    error: event.error ?? 'User sync failed',
                },
            ]);
        }
        if (event.type === 'company_error' || event.type === 'auth0_import_failed') {
            setFailures((current) => [
                ...current,
                {
                    companyId: event.companyId,
                    error: formatEventError(event),
                },
            ]);
        }
        if (
            event.type === 'job_complete' ||
            event.type === 'job_stopped' ||
            event.type === 'job_stop_requested'
        ) {
            setJob((current) =>
                current
                    ? {
                          ...current,
                          status:
                              event.type === 'job_complete'
                                  ? 'completed'
                                  : event.type === 'job_stopped'
                                    ? 'stopped'
                                    : 'stopping',
                          completedCompanies:
                              typeof event.completedCompanies === 'number'
                                  ? event.completedCompanies
                                  : current.completedCompanies,
                          failedCompanies:
                              typeof event.failedCompanies === 'number'
                                  ? event.failedCompanies
                                  : current.failedCompanies,
                      }
                    : current,
            );
        }
    };

    const startSelectedJob = async () => {
        setStatusMessage(null);
        setFailures([]);
        setEvents([]);
        cleanupRef.current?.();
        setIsStartingStream(true);
        try {
            cleanupRef.current = await startJobStream(
                {
                    companyIds: Array.from(selectedCompanyIds),
                    unsyncedOnly,
                },
                handleEvent,
                (error) => setStatusMessage(error.message),
                () =>
                    setStatusMessage(
                        'Bulk Auth0 sync stream finished. Refresh candidates to see what remains.',
                    ),
            );
        } finally {
            setIsStartingStream(false);
        }
    };

    const stopCurrentJob = async () => {
        if (!job) {
            return;
        }
        const stopped = await stopJob.mutateAsync(job.jobId);
        setJob(stopped);
        setStatusMessage('Stop requested. The current company will finish before the job exits.');
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="space-y-4 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-neutral-950">
                                Auth0 Bulk Sync Hub
                            </h2>
                            <p className="text-sm text-neutral-600">
                                Sync selected companies sequentially. Each company uses one Auth0
                                bulk import job for eligible users, and Auth0 user emails are
                                suppressed for this internal migration flow.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => candidatesQuery.refetch()}
                            disabled={candidatesQuery.isFetching || isRunning}
                        >
                            <RefreshCw className="mr-2 size-4" />
                            {candidatesQuery.isFetching ? 'Refreshing...' : 'Refresh candidates'}
                        </Button>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="checkbox"
                            checked={unsyncedOnly}
                            disabled={isRunning}
                            onChange={(event) => {
                                setUnsyncedOnly(event.target.checked);
                                clearSelection();
                                setCandidatePage(1);
                            }}
                        />
                        Only include users without Auth0 subject IDs
                    </label>

                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="checkbox"
                            checked={hideUnnamedCompanies}
                            disabled={isRunning}
                            onChange={(event) => {
                                setHideUnnamedCompanies(event.target.checked);
                                clearSelection();
                            }}
                        />
                        Hide unnamed or generated companies
                    </label>

                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={selectAll}
                            disabled={isRunning}
                        >
                            Select all eligible companies
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={clearSelection}
                            disabled={isRunning}
                        >
                            Clear
                        </Button>
                        <Button
                            type="button"
                            onClick={() => void startSelectedJob()}
                            disabled={
                                isRunning || selectedCompanyIds.size === 0 || isStartingStream
                            }
                        >
                            {isStartingStream
                                ? 'Starting...'
                                : `Start sync for ${selectedCompanyIds.size} companies`}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => void stopCurrentJob()}
                            disabled={!isRunning || job?.status === 'stopping' || stopJob.isPending}
                        >
                            Stop after current company
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {statusMessage && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
                    {statusMessage}
                </div>
            )}

            {job && (
                <Card>
                    <CardContent className="space-y-3 p-4">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h3 className="font-semibold text-neutral-950">Current sync job</h3>
                                <p className="text-sm text-neutral-500">
                                    {job.jobId} · {job.status}
                                </p>
                            </div>
                            <div className="text-sm text-neutral-600">
                                {completedCompanies} successful, {failedCompanies} failed of{' '}
                                {totalCompanies}
                            </div>
                        </div>
                        <div className="text-sm text-neutral-600">
                            Users: {runProgress.syncedUsers} synced, {runProgress.skippedUsers}{' '}
                            skipped, {runProgress.failedUsers} failed of {runProgress.totalUsers}
                        </div>
                        <div className="h-3 overflow-hidden rounded-full bg-neutral-200">
                            <div
                                className="h-full bg-[#276db0]"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <div className="text-xs text-neutral-500">{progressPercent}% complete</div>
                    </CardContent>
                </Card>
            )}

            <CandidateTable
                candidates={pagedCandidates}
                collapsed={candidateListCollapsed}
                currentPage={safeCandidatePage}
                disabled={isRunning}
                filteredCount={filteredCandidates.length}
                onCollapseToggle={() => setCandidateListCollapsed((current) => !current)}
                onPageChange={setCandidatePage}
                selectedCompanyIds={selectedCompanyIds}
                onToggleCompany={toggleCompany}
                totalCount={candidates.length}
                totalPages={totalCandidatePages}
            />
            <JobDetailPanel events={events} failures={failures} />
        </div>
    );
}

function CandidateTable({
    candidates,
    collapsed,
    currentPage,
    disabled,
    filteredCount,
    onCollapseToggle,
    onPageChange,
    onToggleCompany,
    selectedCompanyIds,
    totalCount,
    totalPages,
}: {
    candidates: AdminAuth0BulkSyncCandidate[];
    collapsed: boolean;
    currentPage: number;
    disabled: boolean;
    filteredCount: number;
    onCollapseToggle: () => void;
    onPageChange: (page: number) => void;
    onToggleCompany: (companyId: number) => void;
    selectedCompanyIds: Set<number>;
    totalCount: number;
    totalPages: number;
}) {
    const firstRow = filteredCount === 0 ? 0 : (currentPage - 1) * CANDIDATE_PAGE_SIZE + 1;
    const lastRow = Math.min(currentPage * CANDIDATE_PAGE_SIZE, filteredCount);

    return (
        <Card>
            <CardContent className="space-y-4 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h3 className="font-semibold text-neutral-950">Eligible companies</h3>
                        <p className="text-sm text-neutral-500">
                            Re-running later naturally picks up whatever eligible users remain.
                            Showing {filteredCount} of {totalCount} candidates.
                        </p>
                    </div>
                    <Button type="button" variant="outline" onClick={onCollapseToggle}>
                        {collapsed ? 'Show company list' : 'Hide company list'}
                    </Button>
                </div>

                {!collapsed && (
                    <>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Select</TableHead>
                                    <TableHead>Company</TableHead>
                                    <TableHead>Org</TableHead>
                                    <TableHead>Eligible users</TableHead>
                                    <TableHead>Unsynced users</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {candidates.map((candidate) => (
                                    <TableRow key={candidate.companyId}>
                                        <TableCell>
                                            <input
                                                type="checkbox"
                                                checked={selectedCompanyIds.has(
                                                    candidate.companyId,
                                                )}
                                                disabled={disabled}
                                                onChange={() =>
                                                    onToggleCompany(candidate.companyId)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium text-neutral-950">
                                                {candidate.name || 'Unnamed company'}
                                            </div>
                                            <div className="text-xs text-neutral-500">
                                                Company ID {candidate.companyId}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {candidate.organizationId ?? 'Not synced yet'}
                                        </TableCell>
                                        <TableCell>{candidate.eligibleUserCount}</TableCell>
                                        <TableCell>{candidate.unsyncedUserCount}</TableCell>
                                    </TableRow>
                                ))}
                                {candidates.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-sm text-neutral-500">
                                            No eligible companies found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {filteredCount > CANDIDATE_PAGE_SIZE && (
                            <div className="flex flex-col gap-2 rounded-xl border bg-white px-4 py-3 text-sm text-neutral-600 sm:flex-row sm:items-center sm:justify-between">
                                <span>
                                    Showing {firstRow}-{lastRow} of {filteredCount}
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={currentPage === 1}
                                        onClick={() => onPageChange(currentPage - 1)}
                                    >
                                        Previous
                                    </Button>
                                    <span>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={currentPage === totalPages}
                                        onClick={() => onPageChange(currentPage + 1)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                        {filteredCount <= CANDIDATE_PAGE_SIZE && filteredCount > 0 && (
                            <div className="rounded-xl border bg-white px-4 py-3 text-sm text-neutral-600">
                                Showing {firstRow}-{lastRow} of {filteredCount}
                            </div>
                        )}
                    </>
                )}

                {collapsed && (
                    <div className="rounded-xl border bg-white px-4 py-3 text-sm text-neutral-600">
                        Candidate list hidden. {filteredCount} companies match the current filters.
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function isUnnamedCompany(candidate: AdminAuth0BulkSyncCandidate): boolean {
    const name = candidate.name.trim();
    return name === '' || /^company-\d+$/i.test(name);
}

function JobDetailPanel({
    events,
    failures,
}: {
    events: AdminAuth0BulkSyncEvent[];
    failures: FailureRow[];
}) {
    const importEvents = events.filter((event) => event.type.startsWith('auth0_import_'));
    return (
        <div className="grid gap-4 lg:grid-cols-2">
            <Card>
                <CardContent className="space-y-3 p-4">
                    <h3 className="font-semibold text-neutral-950">Auth0 import jobs</h3>
                    <div className="space-y-2 text-sm">
                        {importEvents.slice(-12).map((event, index) => (
                            <div
                                key={`${event.type}-${event.timestamp ?? index}`}
                                className="rounded-lg border p-3"
                            >
                                <div className="font-medium text-neutral-950">{event.type}</div>
                                <div className="text-neutral-600">
                                    {event.jobId}
                                    {typeof event.externalId === 'string'
                                        ? ` · ${event.externalId}`
                                        : ''}
                                    {typeof event.submittedUsers === 'number'
                                        ? ` · ${event.submittedUsers} users`
                                        : ''}
                                </div>
                            </div>
                        ))}
                        {importEvents.length === 0 && (
                            <p className="text-neutral-500">
                                Import job details will appear as each company runs.
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="space-y-3 p-4">
                    <h3 className="font-semibold text-neutral-950">Failures to revisit</h3>
                    <div className="space-y-2 text-sm">
                        {failures.map((failure, index) => (
                            <div
                                key={`${failure.companyId ?? 'job'}-${failure.userId ?? index}`}
                                className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800"
                            >
                                <div className="font-medium">
                                    Company {failure.companyId ?? 'unknown'}
                                    {failure.userId ? ` · User ${failure.userId}` : ''}
                                </div>
                                <div>{failure.email}</div>
                                <div>{failure.error}</div>
                            </div>
                        ))}
                        {failures.length === 0 && (
                            <p className="text-neutral-500">
                                No failures reported for the current run.
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function formatEventError(event: AdminAuth0BulkSyncEvent): string {
    if (typeof event.error === 'string' && event.error.trim()) {
        return event.error;
    }
    if (event.summary !== undefined) {
        try {
            return JSON.stringify(event.summary);
        } catch {
            return String(event.summary);
        }
    }
    return `${event.type} failed`;
}

function calculateRunProgress(
    events: AdminAuth0BulkSyncEvent[],
    job: AdminAuth0BulkSyncJobSnapshot | null,
    selectedCompanyCount: number,
): RunProgress {
    const successfulCompanyIds = new Set<number>();
    const failedCompanyIds = new Set<number>();
    const userSummariesByCompany = new Map<number, UserSummaryCounts>();
    const importFailuresByCompany = new Map<number, number>();
    let totalCompanies = job?.totalCompanies ?? selectedCompanyCount;

    for (const event of events) {
        if (event.type === 'job_start' && typeof event.totalCompanies === 'number') {
            totalCompanies = event.totalCompanies;
        }

        if (event.type === 'company_error') {
            const companyId = eventCompanyId(event);
            if (companyId !== undefined) {
                failedCompanyIds.add(companyId);
                successfulCompanyIds.delete(companyId);
            }
        }

        if (event.type === 'auth0_import_status' || event.type === 'auth0_import_complete') {
            const companyId = eventCompanyId(event);
            const failedImportUsers = summaryNumber(event.summary, 'failed');
            if (companyId !== undefined && failedImportUsers > 0) {
                failedCompanyIds.add(companyId);
                successfulCompanyIds.delete(companyId);
                importFailuresByCompany.set(
                    companyId,
                    Math.max(importFailuresByCompany.get(companyId) ?? 0, failedImportUsers),
                );
            }
        }

        if (event.type === 'auth0_import_failed') {
            const companyId = eventCompanyId(event);
            if (companyId !== undefined) {
                failedCompanyIds.add(companyId);
                successfulCompanyIds.delete(companyId);
            }
        }

        if (event.type === 'company_complete') {
            const result = eventResult(event);
            const companyId = eventCompanyId(event) ?? resultCompanyId(result);
            if (companyId === undefined) {
                continue;
            }

            const users = resultUsers(result);
            if (users) {
                userSummariesByCompany.set(companyId, users);
            }

            if (users?.success === false || failedCompanyIds.has(companyId)) {
                failedCompanyIds.add(companyId);
                successfulCompanyIds.delete(companyId);
            } else {
                successfulCompanyIds.add(companyId);
            }
        }

        if (event.type === 'job_complete' || event.type === 'job_stopped') {
            totalCompanies = eventNumber(event, 'totalCompanies') ?? totalCompanies;
        }
    }

    const progress = {
        totalCompanies,
        completedCompanies: successfulCompanyIds.size,
        failedCompanies: failedCompanyIds.size,
        totalUsers: 0,
        syncedUsers: 0,
        skippedUsers: 0,
        failedUsers: 0,
    };

    for (const users of userSummariesByCompany.values()) {
        progress.totalUsers += users.total;
        progress.syncedUsers += users.synced;
        progress.skippedUsers += users.skipped;
        progress.failedUsers += users.failed;
    }

    for (const [companyId, failedUsers] of importFailuresByCompany) {
        if (userSummariesByCompany.has(companyId)) {
            continue;
        }
        progress.totalUsers += failedUsers;
        progress.failedUsers += failedUsers;
    }

    return progress;
}

type UserSummaryCounts = {
    success: boolean;
    total: number;
    synced: number;
    skipped: number;
    failed: number;
};

function eventCompanyId(event: AdminAuth0BulkSyncEvent): number | undefined {
    return typeof event.companyId === 'number' ? event.companyId : undefined;
}

function eventNumber(event: AdminAuth0BulkSyncEvent, key: string): number | undefined {
    const value = event[key];
    return typeof value === 'number' ? value : undefined;
}

function eventResult(event: AdminAuth0BulkSyncEvent): Record<string, unknown> | undefined {
    return isRecord(event.result) ? event.result : undefined;
}

function resultCompanyId(result: Record<string, unknown> | undefined): number | undefined {
    return typeof result?.companyId === 'number' ? result.companyId : undefined;
}

function resultUsers(result: Record<string, unknown> | undefined): UserSummaryCounts | undefined {
    if (!isRecord(result?.users)) {
        return undefined;
    }
    const users = result.users;
    if (
        typeof users.success !== 'boolean' ||
        typeof users.total !== 'number' ||
        typeof users.synced !== 'number' ||
        typeof users.skipped !== 'number' ||
        typeof users.failed !== 'number'
    ) {
        return undefined;
    }
    return {
        success: users.success,
        total: users.total,
        synced: users.synced,
        skipped: users.skipped,
        failed: users.failed,
    };
}

function summaryNumber(summary: unknown, key: string): number {
    if (!isRecord(summary)) {
        return 0;
    }
    const value = summary[key];
    return typeof value === 'number' ? value : 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
